/**
 * "Is this line a row off a nutrition panel rather than an ingredient?"
 *
 * Three recipes in the catalog carry their whole panel inside
 * `extendedIngredients` — eight rows each of "Calories 316", "Sodium 385 mg",
 * "Fibres 1 g" — because the scraper picked up the nutrition table along with
 * the ingredient list. `isLikelyIngredient` already refused them at the grocery
 * layer, so they never reached a shopping list, but the recipe screen renders
 * whatever is stored and showed them as checkable ingredients (#594 follow-up).
 *
 * The two tiers below are the ones the grocery filter arrived at, kept because
 * they encode a real distinction:
 *
 *  - Panel-only terms ("calories", "sodium", "cholestérol") never open an
 *    ingredient line, so the word alone is enough.
 *  - Macro terms are ordinary groceries — "brown sugar", "bacon fat", "protein
 *    powder" — so they only count as a panel row in the panel's shape, the
 *    nutrient followed by a mass. A real ingredient puts its quantity first
 *    ("500 g de sucre") or trails it after the food ("Sucre 500 g"), and both
 *    survive.
 */

/** Non-breaking spaces are everywhere in French nutrition tables. */
function tidy(text: string): string {
  return text
    .replace(/[   ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Words that only ever appear on a nutrition panel. */
const PANEL_ONLY =
  /\b(calories?|sodium|cholest[ée]rol|nutrition facts|nutritional info|valeurs? nutritives?|valeurs? quotidiennes?|daily value|serving size)\b/i;

/**
 * A macro nutrient followed by its mass — "Protéines 31 g", "Protein 25 g".
 * Anchored at the start so "2 tbsp fish sauce, 5 g protein per serving" is not
 * mistaken for a panel row.
 */
const MACRO_WITH_MASS =
  /^(?:prot[ée]ines?|protein|glucides|carbs?|carbohydrates?|fibres?|fiber|dietary fib(?:re|er)|sucres|sugars)\b[\s:]*\d/i;

/**
 * The compound forms a panel uses for fat, where the nutrient word and its
 * number are not adjacent: "Total gras 19 g", "Gras saturés 4 g".
 *
 * `gras` and `fat` are deliberately absent on their own — "bacon fat" and
 * "duck fat, for frying" are food. Likewise French `sucre` singular is sugar
 * you buy; the panel writes `Sucres`.
 *
 * The English spellings matter as much as the French: the app translates the
 * catalog, so a French panel is read as "Total Carbohydrates 5 g". That row was
 * still on screen after the first pass caught all seven of its siblings.
 */
const FAT_PANEL_ROW =
  /^(?:total\s+(?:gras|glucides|sucres|fat|carbs?|carbohydrates?|sugars?|fib(?:re|er)s?)|gras\s+(?:satur[ée]s?|trans|insatur[ée]s?)|saturated\s+fat|trans\s+fat|unsaturated\s+fat)\b/i;

export function isNutritionLine(text: string): boolean {
  if (!text) return false;
  const line = tidy(text);
  if (!line) return false;
  return PANEL_ONLY.test(line) || MACRO_WITH_MASS.test(line) || FAT_PANEL_ROW.test(line);
}
